import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { EWalletIcon } from '@/components/UI/Icons/Icons';
import { Button } from '@/components/UI/Button/Button';
import {
  OTHER_ADD_BALANCE_OPTIONS,
  OTHER_PAYMENT_OPTIONS,
  STEPS_DATA,
} from '@/components/Payment/PaymentInTerminals.constants';
import { TerminalPaymentTargetType } from '@/api/types/payments.types';
import * as S from '@/components/Payment/PaymentInTerminals.styles';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { formatNumber, formatPhoneNumberFromBE } from '@/utils/helpers';
import { AdKind } from '@/api/types/common.types';
import { AdService } from '@/api/types/services.types';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import {
  getBundleCountLabel,
  getServiceLabel,
  parseHashStringForTerminal,
} from '@/components/Payment/PaymentInTerminals.helpers';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { AdBundle } from '@/api/types/categories.types';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { ModalDrawer } from '@/components/UI/ModalDrawer';

interface PaymentInTerminalProps {
  isOpen: boolean;
  toggle: (state?: boolean) => void;
  service?: AdService | null;
  serviceType?: AdKind | null;
  bundle?: AdBundle | null;
  paymentTargetType?: TerminalPaymentTargetType;
  onOk?: () => void;
  dataTestId?: string;
}

export const PaymentInTerminals = ({
  isOpen,
  toggle,
  service,
  serviceType,
  bundle,
  paymentTargetType = TerminalPaymentTargetType.Ad,
  onOk,
  dataTestId,
}: PaymentInTerminalProps) => {
  const { t, i18n } = useTranslation();
  const { hash } = useUrlAndHashFromPath();
  const hashedValues = parseHashStringForTerminal(hash);
  const finalService = hashedValues ? hashedValues.service : service;
  const finalServiceType = hashedValues
    ? hashedValues.serviceType
    : serviceType;
  const finalPaymentTargetType = hashedValues
    ? hashedValues.paymentTargetType
    : paymentTargetType;
  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);

  const isAddBalance = paymentTargetType === TerminalPaymentTargetType.Balance;

  function onOkClick() {
    toggle();
    onOk?.();
  }

  const otherOptions = (
    isAddBalance ? OTHER_ADD_BALANCE_OPTIONS : OTHER_PAYMENT_OPTIONS
  ).join(', ');

  return (
    <ModalDrawer
      canGoBack
      dataTestId={dataTestId ?? AdPageTestIds.terminalPaymentDrawer.drawer}
      isOpen={isOpen}
      onClose={() => toggle(false)}
      title={t('payment_in_terminals.payment_in_terminals')}
      bottom={
        <S.ActionsContainer>
          <Button
            appearance="secondary"
            isFullWidth
            borderRadius={14}
            onClick={onOkClick}
            data-testid={AdPageTestIds.terminalPaymentDrawer.okButton}
          >
            {t('common.got_it')}
          </Button>
        </S.ActionsContainer>
      }
    >
      <S.Container>
        <S.Box>
          <S.PhoneLabel>
            {t('payment_in_terminals.your_phone_number')}
          </S.PhoneLabel>
          <S.PhoneBox>
            <S.Phone
              $variant="3"
              data-testid={AdPageTestIds.terminalPaymentDrawer.phone}
            >
              {formatPhoneNumberFromBE(
                userInfo?.currentUser?.authenticationPhone
              )}
            </S.Phone>
          </S.PhoneBox>
        </S.Box>
        {finalPaymentTargetType === TerminalPaymentTargetType.Ad &&
          finalService &&
          finalServiceType && (
            <S.Box>
              <S.InfoRow>
                <S.InfoLabel>
                  {t('payment_in_terminals.add_amount')}
                </S.InfoLabel>
                <S.InfoValue
                  data-testid={AdPageTestIds.terminalPaymentDrawer.service}
                >
                  {getServiceLabel({ i18n, finalService, finalServiceType })}
                </S.InfoValue>
                <S.InfoValueBold
                  $variant="4"
                  data-testid={AdPageTestIds.terminalPaymentDrawer.price}
                >
                  {formatNumber(finalService.price, 2)} ₼
                </S.InfoValueBold>
              </S.InfoRow>
            </S.Box>
          )}
        {(!finalService || !finalServiceType) && bundle && (
          <S.Box>
            <S.InfoRow>
              <S.InfoLabel>{t('payment_in_terminals.add_amount')}</S.InfoLabel>
              <S.InfoValue>
                {getBundleCountLabel(i18n, bundle.count)}
              </S.InfoValue>
              <S.InfoValueBold $variant="4">
                {formatNumber(bundle.price, 2)} AZN
              </S.InfoValueBold>
            </S.InfoRow>
          </S.Box>
        )}
        {finalPaymentTargetType === TerminalPaymentTargetType.Invoice ? (
          <S.Box>
            <S.InfoRow>
              <S.InfoLabel>
                {t('payment_in_terminals.invoice_number')}
              </S.InfoLabel>
              {/* TODO replace hardcoded value */}
              <S.InfoValue>1306</S.InfoValue>
            </S.InfoRow>

            <S.InfoRow>
              <S.InfoLabel>
                {t('payment_in_terminals.service_type')}
              </S.InfoLabel>
              {/* TODO replace hardcoded value */}
              <S.InfoValue>Silver Paketi</S.InfoValue>
            </S.InfoRow>

            <S.Divider />
            <S.InfoRow>
              <S.InfoLabel>{t('common.sum')}</S.InfoLabel>
              {/* TODO replace hardcoded value */}
              <S.InfoValue>14,00 ₼</S.InfoValue>
            </S.InfoRow>
          </S.Box>
        ) : null}
        <S.Box>
          <S.Title $variant="3">
            {t('payment_in_terminals.content_title')}
          </S.Title>
          <S.Steps>
            {STEPS_DATA(t).map((step, i) => {
              return (
                <S.Step key={step.title}>
                  <S.StepIcon>{i + 1}</S.StepIcon>
                  <Typography.Title $variant="1">{step.title}</Typography.Title>

                  {step?.substeps?.map(substep => {
                    return (
                      <S.SubStep key={substep.title}>
                        <S.SubStepIcon>
                          <IconBox $filename={substep.icon} />
                        </S.SubStepIcon>
                        <Typography.Body $variant="1">
                          {substep.title}
                        </Typography.Body>
                      </S.SubStep>
                    );
                  })}
                </S.Step>
              );
            })}
          </S.Steps>
          <S.StepsAdditionalInfo>
            <Typography.Body $variant="1">
              {t(
                `payment_in_terminals.${
                  isAddBalance
                    ? 'amount_will_be_added_to_your_account'
                    : 'service_will_be_applied_to_your_ad'
                }`
              )}
            </Typography.Body>
          </S.StepsAdditionalInfo>
        </S.Box>
        <S.Box>
          <S.EWallets>
            <EWalletIcon />
            <S.EWalletsText>
              <Typography.Body $variant="1">
                {t(
                  `payment_in_terminals.${
                    isAddBalance
                      ? 'other_add_balance_options'
                      : 'other_payment_options'
                  }`
                )}
              </Typography.Body>
              <Typography.Title $variant="1">{otherOptions}</Typography.Title>
            </S.EWalletsText>
          </S.EWallets>
        </S.Box>

        {!isAddBalance && (
          <S.Notifications>
            <S.Notification $variant="3">
              {t('payment_in_terminals.notification_1')}
            </S.Notification>
            <S.Divider />
            <S.Notification $variant="3">
              {t('payment_in_terminals.notification_2')}
            </S.Notification>
          </S.Notifications>
        )}
      </S.Container>
    </ModalDrawer>
  );
};
